import { Injectable } from '@angular/core';
import { HttpService } from '../app/utility/http-service';
import { Guid } from 'guid-typescript';
export interface BidRecord {
  id?: Guid | string;
  clientId?: string;

  mode: 'Share' | 'Trade';
  provideOrNeed: 'Have' | 'Need';

  siteEdit: string;
  siteName: string;

  latitude: number;
  longitude: number;

  startDate: Date;
  endDate: Date;
  rate: number;

  canAcceptTrucks?: boolean;
  canAcceptLayflats?: boolean;

  canProvideTrucks?: boolean;
  provideTrucksRange?: number;
  provideTrucksBid?: number;
  provideTrucksCapacity?: number;

  canProvideLayflats?: boolean;
  provideLayflatsRange?: number;
  provideLayflatsBid?: number;
  provideLayflatsCapacity?: number;

  transportRange?: number;

  willingToPay?: 'Willing to pay' | 'Want to be paid';
  bidAmount?: number;
  bidUnits?: string;

  waterQuality?: string;

  tssConditionType?: string;
  tssValue?: number;
  tdsConditionType?: string;
  tdsValue?: number;
  chlorideConditionType?: string;
  chlorideValue?: number;
  bariumConditionType?: string;
  bariumValue?: number;
  calciumCarbonateConditionType?: string;
  calciumCarbonateValue?: number;
  ironConditionType?: string;
  ironValue?: number;
  boronConditionType?: string;
  boronValue?: number;
  hydrogenSulfideConditionType?: string;
  hydrogenSulfideValue?: number;
  normConditionType?: string;
  normValue?: number;
}

export interface WaterRequest {
  id?: string;
  mode?: string; // Share / Trade
  type?: 'Have' | 'Need';
  context?: 'Share' | 'Trade';
  provideOrNeed?: 'Have' | 'Need';
  siteEdit?: string;
  siteName?: string;
  wellPad?: string;
  wellName?: string;
  latitude?: number | string;
  longitude?: number | string;
  canAcceptTrucks?: boolean;
  canAcceptLayflats?: boolean;
  startDate?: string | Date;
  endDate?: string | Date;
  rateBpd?: number;
  rate?: number; 
  trucksCheckbox?: boolean;
  truckTransportRadius?: number;
  truckCapacity?: number;
  provideTrucksRange?: number;
  provideTrucksBid?: number;
  provideTrucksCapacity?: number;
  canProvideTrucks?: boolean;

  layflatsCheckbox?: boolean;
  layflatsTransportRadius?: number;
  layflatsCapacity?: number;
  provideLayflatsRange?: number;
  provideLayflatsBid?: number;
  provideLayflatsCapacity?: number;
  canProvideLayflats?: boolean;
  transportRange?: number;
  bidType?: string;
  bidAmount?: number;
  bidUnits?: string;
  willingToPay?: string;
  wantToBePaid?: boolean;
  totalBid?: number; 
  tssLimit?: string;
  tssMeasureValue?: string | number;
  tssConditionType?: string;
  tssValue?: number;

  tdsLimit?: string;
  tdsMeasureValue?: string | number;
  tdsConditionType?: string;
  tdsValue?: number;

  chlorideLimit?: string;
  chlorideMeasureValue?: string | number;
  chlorideConditionType?: string;
  chlorideValue?: number;

  bariumLimit?: string;
  bariumMeasureValue?: string | number;
  bariumConditionType?: string;
  bariumValue?: number;

  calciumCarbonateLimit?: string;
  calciumCarbonateMeasureValue?: string | number;
  calciumCarbonateConditionType?: string;
  calciumCarbonateValue?: number;

  ironLimit?: string;
  ironMeasureValue?: string | number;
  ironConditionType?: string;
  ironValue?: number;

  boronLimit?: string;
  boronMeasureValue?: string | number;
  boronConditionType?: string;
  boronValue?: number;

  hydrogenSulfideLimit?: string;
  hydrogenSulfideMeasureValue?: string | number;
  hydrogenSulfideConditionType?: string;
  hydrogenSulfideValue?: number;

  normLimit?: string;
  normMeasureValue?: string | number;
  normConditionType?: string;
  normValue?: number;
  clientId?: string;
}

export interface DashboardRecord {
  id: Guid;
  clientId: string;
  clientName: string;
  siteName: string;
  dateRange: string;
  status: string;
  rateBpd: number;
  latitude: number;
  longitude: number;
  expanded: boolean;
  selected: boolean;
  matches: DashboardMatch[];
  mode: 'Share' | 'Trade';
  hasAdminApproval: boolean;
  userName?: string;
}

export interface DashboardMatch {
  matchedClientId: string;
  matchedClientName: string;
  matchedBidId: Guid;
  matchId: Guid;
  dateRange: string;
  rateBpd: number;
  distance: number;
  sourcedWaterSaved: number;
  showUserDetails: boolean;
  userEmail?: string;
  firstName?: string;
  lastName?: string;
  totalVolume?: number;
  totalCost?: number;
}

export interface BidMatch {
  id: string;
  clientId1: string;
  clientId2: string;
  waterBidId1: string;
  waterBidId2: string;
  isApproved1?: boolean;
  isApproved2?: boolean;
  isRejected1?: boolean;
  isRejected2?: boolean;
  decisionUser1?: string;
  decisionUser2?: string;
  matchStart?: string; 
  matchEnd?: string;   
  totalVolume?: number;
  totalCost?: number;
}


export interface SiteInfo {
  name: string;
  latitude: number;
  longitude: number;
}

@Injectable({
  providedIn: 'root'
})
export class WaterRequestService {
  private readonly basePath = 'Aquatrade';

  constructor(private http: HttpService) { }

  dashboardData(
    mode: string,
    haveNeed: string,
    isForApproval: boolean,
    onSuccess: (data: DashboardRecord[]) => void
  ) {
    const uri = `${this.basePath}/DashboardData?mode=${mode}&haveNeed=${haveNeed}&isForApproval=${isForApproval}`;
    this.http.get<DashboardRecord[]>(uri, onSuccess);
  }

  availableSites(clientId?: string, onSuccess?: (data: SiteInfo[]) => void) {
    const uri = `${this.basePath}/AvailableSites${clientId ? `?clientId=${clientId}` : ''}`;
    this.http.get<SiteInfo[]>(uri, onSuccess ?? (() => { }));
  }

  recordInfo(id: string | Guid, clientId: string, onSuccess: (data: BidRecord) => void) {
    const uri = `${this.basePath}/RecordInfo?id=${id}&clientId=${clientId}`;
    this.http.get<BidRecord>(uri, onSuccess);
  }

  addRecord(record: WaterRequest, onSuccess: () => void) {
    const formData = new FormData();
    formData.append('data', JSON.stringify(record));
    this.http.post(`${this.basePath}/AddRecord`, onSuccess, undefined, formData);
  }

  editRecord(record: WaterRequest, onSuccess: () => void) {
    const formData = new FormData();
    formData.append('data', JSON.stringify(record));
    this.http.post(`${this.basePath}/EditRecord`, onSuccess, undefined, formData);
  }

  addOrEditRecord(record: WaterRequest, onSuccess: () => void) {
    if (record.id) {
      this.editRecord(record, onSuccess);
    } else {
      this.addRecord(record, onSuccess);
    }
  }

  doAction(action: string, bidIds: string[], onSuccess: () => void) {
    const formData = new FormData();
    formData.append('data', JSON.stringify({ action, bidIds }));
    this.http.post(`${this.basePath}/DoAction`, onSuccess, undefined, formData);
  }

  approve(matchId: Guid, bidId: Guid, onSuccess: () => void) {
    const formData = new FormData();
    formData.append('matchId', matchId.toString());
    formData.append('bidId', bidId.toString());
    this.http.post(`${this.basePath}/Approve`, onSuccess, undefined, formData);
  }

  adminApproval(clientId: string, bidId: Guid, onSuccess: () => void) {
    const formData = new FormData();
    formData.append('clientId', clientId);
    formData.append('bidId', bidId.toString());
    this.http.post(`${this.basePath}/AdminApproval`, onSuccess, undefined, formData);
  }

  reject(matchId: Guid, bidId: Guid, onSuccess: () => void) {
    const formData = new FormData();
    formData.append('matchId', matchId.toString());
    formData.append('bidId', bidId.toString());
    this.http.post(`${this.basePath}/Reject`, onSuccess, undefined, formData);
  }

  fullyApprovedMatches(onSuccess: (data: BidMatch[]) => void) {
    const uri = `${this.basePath}/FullyApprovedMatches`;
    this.http.get<BidMatch[]>(uri, onSuccess);
  }

}
